import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { EsignatureConfigViewStore } from "../../store";
import { EsignatureConfigView } from "../view/EsignatureConfigView";
import { EsignatureConfigUpdateStore } from "../../store";
import { EsignatureConfigUpdate } from "../update/EsignatureConfigUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type EsignatureConfigModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const EsignatureConfigModalContent: React.FC<EsignatureConfigModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new EsignatureConfigUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new EsignatureConfigViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <EsignatureConfigView
            esignatureConfigViewStore={
              storeInfo.store as EsignatureConfigViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <EsignatureConfigUpdate
            esignatureConfigUpdateStore={
              storeInfo.store as EsignatureConfigUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
